import {
  GetAssessmentQuery,
  useExecuteCodeAgainstTestCasesLazyQuery,
  useGetLatestCodeExecutionQuery,
} from '@gql/gql-types';
import { Alert, Box, Snackbar, useTheme } from '@mui/material';
import { getInterviewApiUrl } from '@utils/environment';
import { formatTime, getTimeColor } from '@utils/format-time';
import { noop } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInterviewCompletion } from '../../hooks/use-interview-completion';
import { useInterviewHooks } from '../../hooks/use-interview-hooks';
import { useScreenRecorderContext } from '../../hooks/use-screen-recorder-context';
import {
  InterviewCompleting,
  InterviewErrorLoading,
  InterviewNetworkConnectionLost,
  InterviewResumeError,
  InterviewScreenSharingRequired,
  InterviewZaraBusy,
} from '../interview/components/interview-state-views';
import { useAudioFlow } from '../interview/hooks/use-audio-flow';
import useAudioRecorder from '../interview/hooks/use-audio-recorder';
import useAvService from '../interview/hooks/use-av-service';
import useChatClient from '../interview/hooks/use-chat-client';
import { useClosePageAlert } from '../interview/hooks/use-close-page-alert';
import useTimeLeft from '../interview/hooks/use-time-left';
import useVideoUploadClient from '../interview/hooks/use-video-upload-client';
import { InteractionState, NetworkState } from '../interview/types';
import { BadStateSubmissionConfirmationModal } from './bad-state-submission-confirmation-modal';
import {
  getConsoleOutputFromExecutionOutput,
  getInitialTestCases,
  processExecutionResult,
} from './live-coding.utils';
import { LiveCodingContentPanels } from './live-coding-content-panels';
import {
  CODE_EXECUTION_SUPPORTED_LANGUAGE_TO_FRONTEND_LANGUAGE,
  CodeExecutionSupportedLanguage,
  TestCase,
} from './types';
import { useCodeExecutionHandler } from './use-code-execution-handler';
import { VideoAndZaraOverlay } from './video-and-zara-overlay';

// Share a screenshot every 3 seconds
const SCREENSHOT_SHARE_INTERVAL_MS = 3 * 1000;

type LiveCodingMainProps = {
  interviewId: string;
  accessToken: string;
  assessment: NonNullable<GetAssessmentQuery['assessment']>;
  requestedProvider?: 'aurora' | 'polaris' | 'zenith';
  onStarted: () => void;
  onCompleted: () => void;
  updateAssessmentCache: (startedAt: Date) => void;
};

const LiveCodingMain = ({
  interviewId,
  accessToken,
  assessment,
  requestedProvider,
  onStarted,
  onCompleted,
  updateAssessmentCache,
}: LiveCodingMainProps) => {
  const audioFlow = useAudioFlow(interviewId);
  // General interview state
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [interactionState, setInteractionState] =
    useState<InteractionState>('idle');
  const [networkState, setNetworkState] = useState<NetworkState>('stable');
  const [shouldStartCompleting, setShouldStartCompleting] =
    useState<boolean>(false);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [showConnectionRecoveredMessage, setShowConnectionRecoveredMessage] =
    useState<boolean>(false);
  const [zaraBusyError, setZaraBusyError] = useState<boolean>(false);
  const [resumeError, setResumeError] = useState<boolean>(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [
    showBadStateSubmissionConfirmModal,
    setShowBadStateSubmissionConfirmModal,
  ] = useState<boolean>(false);
  const [submissionCompleted, setSubmissionCompleted] =
    useState<boolean>(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] =
    useState<boolean>(false);
  const [hasUnviewedConsoleOutput, setHasUnviewedConsoleOutput] =
    useState(false);
  const [isServerTriggeredCompletion, setIsServerTriggeredCompletion] =
    useState<boolean>(false);

  if (!assessment.args?.codeEditorLanguage) {
    throw new Error('No language found in assessment args');
  }

  const problemStatement =
    assessment.template.filteredAttributes?.problemStatement;

  if (!problemStatement) {
    throw new Error('No problem statement found for assessment template');
  }

  const language =
    CODE_EXECUTION_SUPPORTED_LANGUAGE_TO_FRONTEND_LANGUAGE[
      assessment.args.codeEditorLanguage as CodeExecutionSupportedLanguage
    ] || assessment.args.codeEditorLanguage;

  if (!language) {
    throw new Error('Unsupported language found in assessment args');
  }

  const getStartingCode = useCallback(
    (lang: string) =>
      (
        assessment.template.filteredAttributes as {
          perLanguageAttributes: {
            [key: string]: { startingCode: string };
          };
        }
      )?.perLanguageAttributes?.[lang]?.startingCode ?? '',
    [assessment],
  );

  const [code, setCode] = useState<string>('');

  const {
    loading: loadingLatestExecution,
    data: latestExecutionData,
    refetch: refetchLatestExecution,
  } = useGetLatestCodeExecutionQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const latestExecution = data.getLatestCodeExecution;

      // Set the code
      const initialCode = latestExecution?.code
        ? latestExecution.code
        : getStartingCode(assessment.args.codeEditorLanguage);
      setCode(initialCode);

      // Set the test results if we have execution output
      if (latestExecution?.output) {
        const templateTestCases =
          assessment.template.filteredAttributes?.testCases || [];

        try {
          const executionResult = JSON.parse(latestExecution.output);
          const { testCases: restoredTestResults, consoleOutput } =
            processExecutionResult(executionResult, templateTestCases);

          setTestResults(restoredTestResults);

          if (consoleOutput) {
            setConsoleOutput(consoleOutput);
          } else {
            setConsoleOutput(
              getConsoleOutputFromExecutionOutput(latestExecution.output),
            );
          }
        } catch (error) {
          console.error('Failed to parse console output:', error);
        }
      }
    },
  });

  const [testResults, setTestResults] = useState<TestCase[]>(
    getInitialTestCases(
      assessment.template.filteredAttributes?.testCases || [],
    ),
  );
  const [currentTestTab, setCurrentTestTab] = useState(0);
  const [consoleOutput, setConsoleOutput] = useState('');

  const sendPlaybackCompleteRef = useRef<() => void>(() => {});
  const sendPlaybackCompleteStable = useCallback(() => {
    sendPlaybackCompleteRef.current();
  }, []);

  const {
    play,
    interrupt,
    startRecording,
    stopRecording,
    videoStream,
    isPlaying,
    analyserNode,
    destinationAudioStream,
  } = useAvService({
    audioFlow,
    onVideoChunk: (chunk) =>
      isScreenSharingActive ? queueUserCameraChunk(chunk) : noop,
    onPlaybackComplete: () => {
      sendPlaybackCompleteRef.current();
      if (interactionState !== 'recording') {
        setInteractionState('idle');
      }
    },
  });

  const [executeCodeFinal] = useExecuteCodeAgainstTestCasesLazyQuery({
    fetchPolicy: 'network-only',
  });

  const previousExecutionPassedAllTests = useMemo(() => {
    const latestExecution = latestExecutionData?.getLatestCodeExecution;
    if (!latestExecution) {
      return false;
    }
    return (
      latestExecution?.passingTestCases === latestExecution?.totalTestCases
    );
  }, [latestExecutionData]);

  const handleSetShouldStartCompleting = useCallback(
    async (shouldStart: boolean) => {
      if (shouldStart) {
        setIsServerTriggeredCompletion(true);

        // If server-triggered, dont execute code if previous execution exists
        if (latestExecutionData?.getLatestCodeExecution) {
          setShouldStartCompleting(shouldStart);
          return;
        }

        // run the solution against the test cases to make sure the user has a solution submitted
        // even though the time has run out
        try {
          await executeCodeFinal({
            variables: {
              input: { code },
            },
          });
        } catch (error) {
          console.error('Failed to execute code:', error);
          // Ignore the error, we tried sending the code to the backend but it failed
          // we will use latest code execution to evaluate the solution
          // or fail if none is present
        }
      }
      setShouldStartCompleting(shouldStart);
    },
    [code, executeCodeFinal, latestExecutionData],
  );

  const {
    onResponseAudio,
    onResponseInterrupted,
    onConnectionUnstable,
    onConnectionLost,
    onConnectionRecovered,
    onChatClientError,
    onChatClientClosed,
    onChatClientComplete,
    onChatClientCompleted,
  } = useInterviewHooks({
    interviewId,
    setInteractionState,
    onCompleted,
    play,
    interrupt,
    sendPlaybackComplete: sendPlaybackCompleteStable,
    stopRecording,
    setNetworkState,
    setShowConnectionRecoveredMessage,
    setZaraBusyError,
    setResumeError,
    setShouldStartCompleting: handleSetShouldStartCompleting,
    setIsComplete,
  });

  const {
    isScreenSharingActive,
    setChunkHandler,
    startScreenRecording,
    stopScreenRecording,
    captureFrame,
  } = useScreenRecorderContext();

  const {
    isStarted: isInterviewStarted,
    videoUploadUrl,
    screenRecordingUploadUrl,
    startedAt,
    duration,
    inputSampleRate,
    appendInputAudio,
    appendInputImage,
    completeInterview,
    sendPlaybackComplete,
  } = useChatClient({
    audioFlow,
    apiUrl: getInterviewApiUrl() || 'missing-interview-api-url',
    interviewId,
    accessToken,
    requestedProvider,
    onResponseAudio: isScreenSharingActive ? onResponseAudio : noop,
    onResponseInterrupted: onResponseInterrupted,
    onConnectionUnstable: isScreenSharingActive ? onConnectionUnstable : noop,
    onConnectionLost: isScreenSharingActive ? onConnectionLost : noop,
    onConnectionRecovered: isScreenSharingActive ? onConnectionRecovered : noop,
    onClosed: onChatClientClosed,
    onError: onChatClientError,
    onStarted: onStarted,
    onComplete: onChatClientComplete,
    onCompleted: onChatClientCompleted,
    updateAssessmentCache: updateAssessmentCache,
  });

  useEffect(() => {
    audioFlow.playbackSuppressed = !isScreenSharingActive;
  }, [audioFlow, isScreenSharingActive]);

  useEffect(() => {
    sendPlaybackCompleteRef.current = sendPlaybackComplete;
  }, [sendPlaybackComplete]);

  const {
    queueChunk: queueUserCameraChunk,
    finishUpload: finishUserCameraUpload,
  } = useVideoUploadClient({
    videoUploadUrl,
    isUploading: isInterviewStarted,
  });

  const {
    queueChunk: queueScreenRecordingChunk,
    finishUpload: finishScreenRecordingUpload,
    isReadyToUpload: isScreenRecordingReadyToUpload,
  } = useVideoUploadClient({
    videoUploadUrl: screenRecordingUploadUrl,
    isUploading: isInterviewStarted,
  });

  const { cleanupAndCompleteInterview, forceCompleteInterview } =
    useInterviewCompletion({
      setIsCompleting,
      stopRecording,
      finishUserCameraUpload,
      stopScreenRecording,
      finishScreenRecordingUpload,
      completeInterview,
    });

  const handleFinalSubmit = useCallback(async () => {
    setShowBadStateSubmissionConfirmModal(false);
    setIsSubmitting(true);
    await cleanupAndCompleteInterview();
    setSubmissionCompleted(true);
    setShowSuccessSnackbar(true);
    setIsSubmitting(false);
    onCompleted();
  }, [cleanupAndCompleteInterview, onCompleted]);

  const { handleExecutionCompleted } = useCodeExecutionHandler({
    assessment,
    setConsoleOutput,
    setTestResults,
    isSubmitting,
    setShowBadStateSubmissionConfirmModal,
    setHasUnviewedConsoleOutput,
    currentTestTab,
    isServerTriggeredCompletion,
    handleFinalSubmit,
    refetchLatestExecution,
  });

  const [executeCode, { loading: isRunning }] =
    useExecuteCodeAgainstTestCasesLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: handleExecutionCompleted,
      onError: (error) => {
        if (
          error.graphQLErrors.some(
            (e) =>
              (e.extensions?.originalError as { statusCode: number })
                ?.statusCode === 429,
          )
        ) {
          setRateLimitError(error.message);
        } else {
          setConsoleOutput(`GraphQL error:\n${error.message}`);
        }
        if (isSubmitting) {
          setIsSubmitting(false);
        }
      },
    });

  const handleRun = useCallback(async () => {
    executeCode({
      variables: {
        input: {
          code,
        },
      },
    });
  }, [executeCode, code]);

  const handleSubmit = useCallback(async () => {
    // If user has previous execution that passed all tests, just submit
    if (previousExecutionPassedAllTests) {
      setIsSubmitting(true);
      // Wait for 2 seconds to let candidate know the submission is in progress
      setTimeout(() => {
        handleFinalSubmit();
      }, 2000);
      return;
    }

    // If no previous execution, or did not pass all tests, execute code first
    setIsSubmitting(true);
    try {
      await executeCode({
        variables: {
          input: {
            code,
          },
        },
      });
    } catch (error) {
      console.error('Failed to execute code:', error);
      setIsSubmitting(false);
    }
  }, [executeCode, code, previousExecutionPassedAllTests, handleFinalSubmit]);

  useEffect(() => {
    if (isInterviewStarted && isScreenSharingActive) {
      startRecording();
      setInteractionState('recording');
    } else {
      setInteractionState('idle');
    }
  }, [isInterviewStarted, isScreenSharingActive, startRecording]);

  useAudioRecorder({
    audioFlow,
    isRecording:
      isInterviewStarted && networkState !== 'lost' && isScreenSharingActive,
    onAudioChunk: appendInputAudio,
    inputSampleRate,
  });

  useEffect(() => {
    if (
      isScreenRecordingReadyToUpload &&
      isInterviewStarted &&
      networkState !== 'lost' &&
      isScreenSharingActive
    ) {
      startScreenRecording(destinationAudioStream ?? undefined);
      setChunkHandler(queueScreenRecordingChunk);
    } else {
      setChunkHandler(undefined);
    }
  }, [
    isScreenRecordingReadyToUpload,
    isInterviewStarted,
    networkState,
    isScreenSharingActive,
    setChunkHandler,
    queueScreenRecordingChunk,
    startScreenRecording,
    destinationAudioStream,
  ]);

  const frameCaptureIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (
      !isInterviewStarted ||
      networkState === 'lost' ||
      !isScreenSharingActive
    ) {
      return;
    }

    const handleCaptureFrame = async () => {
      const frameDataUrl = await captureFrame();
      if (frameDataUrl) {
        appendInputImage(frameDataUrl);
      }
    };

    // Capture first frame after 5 seconds
    const firstFrameTimeout = setTimeout(async () => {
      await handleCaptureFrame();

      // Start regular interval after the first capture
      frameCaptureIntervalRef.current = setInterval(async () => {
        await handleCaptureFrame();
      }, SCREENSHOT_SHARE_INTERVAL_MS);
    }, 5000);

    return () => {
      clearTimeout(firstFrameTimeout);
      if (frameCaptureIntervalRef.current) {
        clearInterval(frameCaptureIntervalRef.current);
        frameCaptureIntervalRef.current = null;
      }
    };
  }, [
    isInterviewStarted,
    networkState,
    isScreenSharingActive,
    captureFrame,
    appendInputImage,
  ]);

  const timeLeft = useTimeLeft({
    startedAt,
    duration,
  });

  const currentVideoRef = videoRef.current;
  useEffect(() => {
    if (currentVideoRef) {
      currentVideoRef.srcObject = videoStream;
    }
  }, [videoStream, currentVideoRef]);

  useEffect(() => {
    // Wait for the AI to finish talking before completing the interview.
    const readyToFinish = interactionState !== 'aiSpeaking' && !isPlaying;
    if (shouldStartCompleting && readyToFinish) {
      cleanupAndCompleteInterview();
    }
  }, [
    interactionState,
    isPlaying,
    shouldStartCompleting,
    cleanupAndCompleteInterview,
  ]);

  useClosePageAlert({
    showAlert: !isComplete,
  });

  // Handler functions for coding challenge UI
  const timeColor = getTimeColor(timeLeft, {
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
  });

  const handleCodeChange = useCallback((newCode: string) => {
    setCode(newCode);
  }, []);

  if (!isScreenSharingActive && !isCompleting && !isComplete) {
    return <InterviewScreenSharingRequired />;
  }

  if (zaraBusyError) {
    return <InterviewZaraBusy />;
  }

  if (resumeError) {
    return <InterviewResumeError />;
  }

  if (interactionState === 'error') {
    return <InterviewErrorLoading />;
  }

  if (isCompleting) {
    return <InterviewCompleting />;
  }

  if (networkState === 'lost') {
    return <InterviewNetworkConnectionLost />;
  }

  if (!isInterviewStarted) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <VideoAndZaraOverlay
          videoRef={videoRef}
          isInterviewStarted={isInterviewStarted}
          interactionState={interactionState}
          analyserNode={analyserNode}
          assessmentId={interviewId}
          onUserEndedInterview={forceCompleteInterview}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <NetworkStatusAlerts
        networkState={networkState}
        showConnectionRecoveredMessage={showConnectionRecoveredMessage}
      />
      <Snackbar
        open={!!rateLimitError}
        autoHideDuration={6000}
        onClose={() => setRateLimitError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setRateLimitError(null)}
          severity='error'
          sx={{ width: '100%' }}
        >
          {rateLimitError}
        </Alert>
      </Snackbar>

      <LiveCodingContentPanels
        timeLeft={timeLeft}
        taskDescription={problemStatement}
        isRunning={isRunning}
        handleRun={handleRun}
        handleSubmit={handleSubmit}
        language={language}
        code={code}
        handleCodeChange={handleCodeChange}
        testResults={testResults}
        currentTestTab={currentTestTab}
        setCurrentTestTab={setCurrentTestTab}
        timeColor={timeColor}
        formatTime={formatTime}
        consoleOutput={consoleOutput}
        isLoadingCode={loadingLatestExecution && !latestExecutionData}
        submissionCompleted={submissionCompleted}
        isSubmitting={isSubmitting}
        hasUnviewedConsoleOutput={hasUnviewedConsoleOutput}
        setHasUnviewedConsoleOutput={setHasUnviewedConsoleOutput}
      />

      <VideoAndZaraOverlay
        videoRef={videoRef}
        isInterviewStarted={isInterviewStarted}
        interactionState={interactionState}
        analyserNode={analyserNode}
        assessmentId={interviewId}
        onUserEndedInterview={forceCompleteInterview}
      />
      <BadStateSubmissionConfirmationModal
        open={showBadStateSubmissionConfirmModal}
        onClose={() => {
          setShowBadStateSubmissionConfirmModal(false);
          setIsSubmitting(false);
        }}
        onSubmit={handleFinalSubmit}
      />

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity='success'
          sx={{ width: '100%' }}
        >
          Solution submitted successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LiveCodingMain;

const NetworkStatusAlerts = ({
  networkState,
  showConnectionRecoveredMessage,
}: {
  networkState: NetworkState;
  showConnectionRecoveredMessage: boolean;
}) => (
  <>
    <Snackbar
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={networkState === 'unstable'}
    >
      <Alert severity='warning'>
        Unstable network connection, attempting to reconnect...
      </Alert>
    </Snackbar>
    <Snackbar
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={showConnectionRecoveredMessage}
      autoHideDuration={3000}
    >
      <Alert severity='success'>Connection recovered</Alert>
    </Snackbar>
  </>
);
